/**
 * MUTAGIM.SHOP — Upsell & Cross-sell Sections (Phase 15)
 * "Complète le look" et "Les clients ont aussi acheté"
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Shirt, Users, ShoppingBag, Sparkles } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import { useCart } from '../contexts/CartContext';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper pour formater les URLs d'images
const getImageUrl = (img) => {
  if (!img) return '/placeholder-product.jpg';
  if (img.startsWith('/api/')) return `${API_URL}${img}`;
  if (img.startsWith('http')) return img;
  return img;
};

// Section "Complète le look" - Produits complémentaires
export function CompleteLookSection({ productId, className }) {
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);
  const lang = i18n.language;

  useEffect(() => {
    if (!productId) return;
    
    const fetchUpsell = async () => {
      try {
        const res = await fetch(`${API_URL}/api/promotions/upsell/${productId}?limit=4`);
        if (res.ok) {
          const data = await res.json();
          setProducts(data.products || []);
        }
      } catch (err) {
        console.error('Error fetching upsell:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchUpsell();
  }, [productId]);

  const handleAdd = async (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    setAdding(product.id);
    try {
      await addToCart(product.id);
      toast.success(t('cart.added', 'Ajouté au panier'));
    } catch (err) {
      toast.error('Erreur');
    } finally {
      setAdding(null);
    }
  };

  if (loading || products.length === 0) return null;

  return (
    <section className={cn("mt-12", className)} data-testid="complete-look-section">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-mutagim-red/20 rounded-lg">
          <Shirt className="w-5 h-5 text-mutagim-red" />
        </div>
        <h2 className="text-xl font-bold text-white">
          {t('promotions.completeLook', 'Complète le look')}
        </h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {products.map((product) => (
          <Link
            key={product.id}
            to={`/product/${product.slug}`}
            className="group bg-gray-900/50 rounded-lg overflow-hidden border border-gray-800 hover:border-mutagim-red/50 transition-all"
          >
            <div className="aspect-square overflow-hidden">
              <img
                src={getImageUrl(product.image)}
                alt={product.name?.[lang] || ''}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              />
            </div>
            <div className="p-3">
              <h3 className="text-white text-sm font-medium line-clamp-2 mb-2 min-h-[2.5rem]">
                {product.name?.[lang] || product.name?.fr || 'Produit'}
              </h3>
              <div className="flex items-center justify-between">
                <span className="text-mutagim-red font-bold">
                  {formatPrice({ EUR: product.price })}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => handleAdd(product, e)}
                  disabled={adding === product.id}
                  className="text-gray-400 hover:text-white hover:bg-mutagim-red/20"
                >
                  <ShoppingBag className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// Section "Les clients ont aussi acheté" - Cross-sell basé sur les commandes
export function CustomersAlsoBoughtSection({ productId, className }) {
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);
  const lang = i18n.language;

  useEffect(() => {
    if (!productId) return;
    
    const fetchCrossSell = async () => {
      try {
        const res = await fetch(`${API_URL}/api/promotions/cross-sell/${productId}?limit=4`);
        if (res.ok) {
          const data = await res.json();
          setProducts(data.products || []);
        }
      } catch (err) {
        console.error('Error fetching cross-sell:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCrossSell();
  }, [productId]);

  const handleAdd = async (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    setAdding(product.id);
    try {
      await addToCart(product.id);
      toast.success(t('cart.added', 'Ajouté au panier'));
    } catch (err) {
      toast.error('Erreur');
    } finally {
      setAdding(null);
    }
  };

  if (loading || products.length === 0) return null;

  return (
    <section className={cn("mt-12", className)} data-testid="customers-also-bought-section">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-purple-500/20 rounded-lg">
          <Users className="w-5 h-5 text-purple-400" />
        </div>
        <h2 className="text-xl font-bold text-white">
          {t('promotions.customersAlsoBought', 'Les clients ont aussi acheté')}
        </h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {products.map((product) => (
          <Link
            key={product.id}
            to={`/product/${product.slug}`}
            className="group bg-gray-900/50 rounded-lg overflow-hidden border border-gray-800 hover:border-purple-500/50 transition-all"
          >
            <div className="aspect-square overflow-hidden relative">
              <img
                src={getImageUrl(product.image)}
                alt={product.name?.[lang] || ''}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              />
              <div className="absolute top-2 left-2">
                <span className="bg-purple-500/80 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  {t('promotions.popular', 'Populaire')}
                </span>
              </div>
            </div>
            <div className="p-3">
              <h3 className="text-white text-sm font-medium line-clamp-2 mb-2 min-h-[2.5rem]">
                {product.name?.[lang] || product.name?.fr || 'Produit'}
              </h3>
              <div className="flex items-center justify-between">
                <span className="text-mutagim-red font-bold">
                  {formatPrice({ EUR: product.price })}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => handleAdd(product, e)}
                  disabled={adding === product.id}
                  className="text-gray-400 hover:text-white hover:bg-purple-500/20"
                >
                  <ShoppingBag className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default { CompleteLookSection, CustomersAlsoBoughtSection };
